import { Link } from 'react-router-dom';
import { ExternalLink, Search } from 'lucide-react';
import type { Job } from '@core/types';

const CHANNELS = [
  { name: 'LinkedIn', hint: 'Boolean search on title + client keywords' },
  { name: 'Naukri / Indeed', hint: 'Match JD must-have skills in resume title' },
  { name: 'Referrals', hint: 'Share anonymized brief with client employees' },
  { name: 'GitHub / Portfolio', hint: 'For engineering roles — stack match' },
];

export function RoleFitSourcingTab({ jobs }: { jobs: Job[] }) {
  return (
    <div className="space-y-6 p-4 text-sm">
      <p className="text-gray-400">
        Use this view as a sourcing cockpit: for each open role, hit the usual channels with the same filters you use on
        the matrix. Copy the public JD link if your board supports it.
      </p>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">Suggested channels</h3>
        <ul className="grid gap-2 sm:grid-cols-2">
          {CHANNELS.map((c) => (
            <li key={c.name} className="rounded-lg border border-gray-800 bg-gray-900/40 p-3">
              <p className="font-medium text-gray-200">{c.name}</p>
              <p className="text-xs text-gray-500 mt-1">{c.hint}</p>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3 flex items-center gap-2">
          <Search className="h-3.5 w-3.5" /> Roles in current filter ({jobs.length})
        </h3>
        {jobs.length === 0 ? (
          <p className="text-gray-500">No jobs match your filters — adjust client or status on the Matrix tab.</p>
        ) : (
          <ul className="space-y-2">
            {jobs.map((j) => (
              <li
                key={j.id}
                className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-gray-800 bg-gray-900/30 px-3 py-2"
              >
                <div>
                  <Link to={`/jobs/${j.id}`} className="font-medium text-brand-400 hover:underline">
                    {j.title}
                  </Link>
                  <p className="text-xs text-gray-500">{j.client?.name ?? 'Client'} · {(j.skills_required ?? []).slice(0, 4).join(', ')}
                    {(j.skills_required?.length ?? 0) > 4 ? '…' : ''}
                  </p>
                </div>
                <div className="flex gap-2">
                  <Link to={`/jobs/${j.id}`} className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1">
                    <ExternalLink className="h-3.5 w-3.5" /> JD & skills
                  </Link>
                  {j.jd_url && (
                    <a
                      href={j.jd_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn-secondary text-xs py-1.5 inline-flex items-center gap-1"
                    >
                      External posting
                    </a>
                  )}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
